import React, { useCallback } from 'react';
import { translate } from '@utils/i18n';
import snakeToCapitalize from '@utils/functions/snakeToCapitalize';
import { Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui/molecules/Brushaway';
import { Tag } from '@ui/atoms/Tag';
import { Button } from '@ui/atoms/Button/Button';
import { IssueStatusEnum, ModeIA } from '../../types';
import { useUpdateIssue } from '../../hooks/useUpdateIssue';
import { useUndoStatusIssue } from '../../hooks/useUndoStatusIssue';
import { isNonSuggestableIssueType } from '../../utils/nonSuggestableIssueTypes';
import { getIssueTypeDisplayKey } from '../../utils/getIssueTypeDisplayKey';
import IssueDetailBladeContent from './IssueDetailBladeContent';
import './styles.scss';
const NS = 'features.iim_review.issue_detail_blade';
const CARD_NS = 'features.iim_review.issue_card';
const GRID_NS = 'features.iim_review.issues_grid';
// Blade-specific status→color mapping per Figma: "Detected" is neutral gray
// (the card list uses red for Detected to signal urgency, but in the blade
// the Impact badge already conveys that).
const BLADE_STATUS_VARIANT = {
    [IssueStatusEnum.Detected]: 'gray',
    [IssueStatusEnum.Suggested]: 'purple',
    [IssueStatusEnum.Resolved]: 'green',
    [IssueStatusEnum.Skipped]: 'yellow',
    [IssueStatusEnum.DismissedByTech]: 'gray',
    [IssueStatusEnum.DismissedByBackOffice]: 'blue',
    [IssueStatusEnum.CorrectionSuggested]: 'purple',
    // IP-14540: informational/FYI badge — distinct from actionable statuses.
    [IssueStatusEnum.Notification]: 'pink'
};
export default function IssueDetailBlade({ issue, issues, isOpen, onClose, onNavigateToIssue, inspectionId, modeIA, onViewDeficiency }) {
    var _a, _b;
    const updateIssueMutation = useUpdateIssue({ inspectionId });
    const undoStatusChangeMutation = useUndoStatusIssue({ issueId: (_a = issue === null || issue === void 0 ? void 0 : issue.id) !== null && _a !== void 0 ? _a : 0, inspectionId });
    const currentIndex = issue ? issues.findIndex((i) => i.id === issue.id) : -1;
    const hasNextIssue = currentIndex >= 0 && currentIndex < issues.length - 1;
    const hasPreviousIssue = currentIndex > 0;
    const handleNextIssue = useCallback(() => {
        if (hasNextIssue) {
            onNavigateToIssue(issues[currentIndex + 1]);
        }
    }, [hasNextIssue, issues, currentIndex, onNavigateToIssue]);
    const handlePreviousIssue = useCallback(() => {
        if (hasPreviousIssue) {
            onNavigateToIssue(issues[currentIndex - 1]);
        }
    }, [hasPreviousIssue, issues, currentIndex, onNavigateToIssue]);
    const handleDismiss = useCallback(() => {
        if (!issue)
            return;
        updateIssueMutation.mutate({
            issueId: issue.id,
            status: IssueStatusEnum.DismissedByBackOffice,
            markedForSuggestion: false
        });
    }, [issue, updateIssueMutation]);
    const handleSuggestCorrection = useCallback(() => {
        if (!issue)
            return;
        const status = issue.status || IssueStatusEnum.Detected;
        updateIssueMutation.mutate({
            issueId: issue.id,
            status,
            markedForSuggestion: true
        });
    }, [issue, updateIssueMutation]);
    const handleUndo = useCallback(() => {
        undoStatusChangeMutation.mutate();
    }, [undoStatusChangeMutation]);
    if (!issue)
        return null;
    const isNonSuggestable = isNonSuggestableIssueType(issue.issueType);
    const highImpact = (_b = issue.issueData) === null || _b === void 0 ? void 0 : _b.highImpact;
    const issueTitle = translate(getIssueTypeDisplayKey(issue), {
        namespace: `${GRID_NS}.issue_type_labels`,
        defaultValue: snakeToCapitalize(issue.issueType)
    });
    const statusLabel = issue.markedForSuggestion
        ? translate('correction_suggested', { namespace: CARD_NS })
        : translate(issue.status, { namespace: `${CARD_NS}.status`, defaultValue: issue.status });
    const isActioned = [IssueStatusEnum.DismissedByBackOffice, IssueStatusEnum.Suggested, IssueStatusEnum.Resolved].includes(issue.status) || issue.markedForSuggestion;
    // Figma: actioned states use solid tag color, neutral states (Detected/Skipped) use pastry
    const statusTagColorStyle = isActioned ? 'solid' : 'pastry';
    // Keep action button loading states separate so Undo is always clickable
    // immediately after an optimistic Suggest/Dismiss, even while the API is still in-flight.
    const isActionLoading = updateIssueMutation.isPending;
    const isUndoLoading = undoStatusChangeMutation.isPending;
    const isReadOnly = modeIA === ModeIA.ReadOnly || modeIA === ModeIA.Trial || modeIA === ModeIA.Teaser;
    return (React.createElement(Brushaway, { controlledOpen: isOpen, onClose: onClose, closeOnClickOutside: true, minWidth: 480, className: "issue-detail-blade" },
        React.createElement(BrushawayHeader, { title: issueTitle }),
        React.createElement("div", { className: "issue-detail-blade__header-badges" },
            highImpact !== null && highImpact !== undefined && (React.createElement("div", { className: "issue-detail-blade__badge-group" },
                React.createElement("span", { className: "issue-detail-blade__badge-label" }, translate('impact_label', { namespace: NS, defaultValue: 'Impact:' })),
                React.createElement(Tag, { label: highImpact
                        ? translate('high', { namespace: 'features.iim_review.issues_grid.impact', defaultValue: 'High' })
                        : translate('low', { namespace: 'features.iim_review.issues_grid.impact', defaultValue: 'Low' }), variant: highImpact ? 'red' : 'gray', readOnly: true }))),
            React.createElement("div", { className: "issue-detail-blade__badge-group" },
                React.createElement("span", { className: "issue-detail-blade__badge-label" }, translate('issue_status_label', { namespace: NS, defaultValue: 'Issue Status:' })),
                React.createElement(Tag, { label: statusLabel, variant: issue.markedForSuggestion ? 'purple' : BLADE_STATUS_VARIANT[issue.status] || 'gray', colorStyle: statusTagColorStyle, readOnly: true }))),
        React.createElement(BrushawayBody, null,
            React.createElement(IssueDetailBladeContent, { issue: issue, inspectionId: inspectionId, modeIA: modeIA, onViewDeficiency: onViewDeficiency })),
        React.createElement(BrushawayFooter, { startActions: hasPreviousIssue ? (React.createElement(Button, { variant: "default", onClick: handlePreviousIssue, title: translate('previous_issue', { namespace: NS, defaultValue: 'Previous Issue' }) },
                React.createElement("i", { className: "fa-light fa-arrow-left", "aria-hidden": "true" }),
                React.createElement("span", null, translate('back', { namespace: NS, defaultValue: 'Back' })))) : undefined },
            !isReadOnly &&
                (isActioned ? (React.createElement(Button, { variant: "primary", onClick: handleUndo, disabled: isUndoLoading },
                    React.createElement("i", { className: "fa-light fa-arrows-repeat", "aria-hidden": "true" }),
                    ' ',
                    translate('undo', { namespace: CARD_NS, defaultValue: 'Undo' }))) : (React.createElement(React.Fragment, null,
                    React.createElement(Button, { variant: "secondary", onClick: handleDismiss, disabled: isActionLoading }, translate('dismiss', { namespace: CARD_NS, defaultValue: 'Dismiss' })),
                    !isNonSuggestable && (React.createElement(Button, { variant: "secondary", onClick: handleSuggestCorrection, disabled: isActionLoading }, translate('suggest_correction', { namespace: CARD_NS, defaultValue: 'Suggest Correction' })))))),
            hasNextIssue && (React.createElement(Button, { variant: "primary", onClick: handleNextIssue, title: translate('next_issue', { namespace: NS, defaultValue: 'Next Issue' }) },
                React.createElement("span", null, translate('next', { namespace: NS, defaultValue: 'Next' })),
                React.createElement("i", { className: "fa-light fa-arrow-right", "aria-hidden": "true" }))))));
}
